import React, { useEffect, useState, useRef } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import ScoreTable from '../../../components/tables/ScoreTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useScoreApi } from '../../../hooks/useScoreApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import {
  AuthDTO,
  EmailMessageDTO,
  ImpersonationDTO,
  MemberFirearmDTO,
  NoteDTO,
  ScoreDTO,
} from '../../../services/apiClient';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRoles, getUserId } from '../../../utils/tokenUtils';
import { useSearchState } from '../../../utils/searchStateUtils/useSearchState';
import { useAuthApi } from '../../../hooks/useAuthApi';
import { useScoreTypes } from '../../../hooks/useScoreTypeApi';
import { useScoreStatuses } from '../../../hooks/useScoreStatusApi';
import { useExerciseTypeApi } from '../../../hooks/useExerciseTypeApi';
import { useMemberFirearmApi } from '../../../hooks/useMemberFirearmApi';
import { useActivityTypes } from '../../../hooks/useActivityTypeApi';
import { useViewMode } from '../../../utils/viewModeUtils/useViewMode';
import { ViewToggle } from '../../../components/common/ViewToggle';
import { ViewModeConstants } from '../../../constants/ViewModeConstants';
import ScoreDataCard from '../../../components/dataCards/ScoreDataCard';
import Drawer from '../../../components/common/Drawer';
import FeedbackForm from '../../../components/forms/FeedbackForm';
import { ComponentConstants } from '../../../constants/ComponentConstants';
import { ScoreStatusConstants } from '../../../constants/ScoreStatusConstants';
import { MemberStatusConstants } from '../../../constants/MemberStatusConstants';
import { DrawerViewConstants, ScoreDrawerViews } from '../../../constants/DrawerViewConstants';
import { useEmailMessageApi } from '../../../hooks/useEmailMessageApi';
import EmailDrawerContent from '../../../components/common/EmailDrawerContent';
import { useImpersonation } from '../../../hooks/useImpersonation';
import { useEmailHandlers } from '../../../hooks/useEmailHandlers';
import { useNoteApi } from '../../../hooks/useNoteApi';
import AddNote from '../../../components/drawer/AddNote';
import ViewNoteHistory from '../../../components/drawer/ViewNoteHistory';
import MemberQuickDetails from '../../../components/drawer/MemberQuickDetails';
import GeneralSearch from '../../../components/common/GeneralSearch';
import { ScoreTypeConstants } from '../../../constants/ScoreTypeConstants';
import { ChevronDown } from 'lucide-react';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';
import ButtonLegendCta from '../../../components/common/ButtonLegendCta';
import { getButtonLegendItems } from '../../../utils/buttonLegendDisplayUtils';

const legendItems = getButtonLegendItems('score');

const ScoreList: React.FC = () => {
  const { edit, search, remove, extract, restoreFromDeleted, updateStatus, getDistinctYears, isLoading, error } = useScoreApi();
  const [items, setItems] = useState<ScoreDTO[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  // Search state persistence
  const {
    currentPage,
    pageSize,
    sortBy,
    sortDescending,
    searchFilters,
    showAdvanced,
    setCurrentPage,
    setPageSize,
    setSortBy,
    setSortDescending,
    setSort,
    setSearchFilters,
    setShowAdvanced,
    commitSearch,
    clearSearchState,
  } = useSearchState('scores', {
    currentPage: 1,
    pageSize: 10,
    sortBy: 'CreatedUtc',
    sortDescending: true,
    searchFilters: {},
    showAdvanced: false,
  });

  const [totalRecords, setTotalRecords] = useState(0);
  const [drawerTitle, setDrawerTitle] = useState<string>();
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [drawerView, setDrawerView] = useState<ScoreDrawerViews>();
  const [selectedScore, setSelectedScore] = useState<ScoreDTO>({});
  const [defaultStatusId, setDefaultStatusId] = useState<string>();
  const [drawerSize, setDrawerSize] = useState<'small' | 'large'>('large');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedScoreIds, setSelectedScoreIds] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState<boolean>(false);
  const bulkActionsRef = useRef<HTMLDivElement>(null);
  const statusFieldName = 'scoreStatusId';

  const roles = getRoles();
  const navigate = useNavigate();
  const location = useLocation();

  const { impersonate } = useImpersonation();
  const { sendEmail, isLoading: isSendingEmail } = useEmailMessageApi();
  const { create: createNote, isLoading: isSubmittingNote } = useNoteApi();

  const { view, toggle } = useViewMode(
    'scores',
    roles?.includes('User') ? ViewModeConstants.DATA_CARDS : ViewModeConstants.TABLE,
  );

  const statusFromUrl = location.pathname.includes('/admin/scores/')
    ? location.pathname.split('/admin/scores/')[1]?.toLowerCase() || ''
    : '';

  const getFiltersFromStatus = (status: string) => {
    switch (status.toLowerCase()) {
      case 'evaluation':
        return {
          scoreTypeId: ScoreTypeConstants.COURSE_PRACTICAL_EVALUATION,
          isDeleted: false,
        };
      case 'competition':
        return {
          scoreTypeId: ScoreTypeConstants.COMPETITION,
          isDeleted: false,
        };
      case 'in-house':
        return {
          scoreTypeId: ScoreTypeConstants.IN_HOUSE,
          isDeleted: false,
        };
      case 'deleted':
        return { isDeleted: true };
      default:
        return { isDeleted: false };
    }
  };

  const getStatusDisplayName = (status: string) => {
    switch (status.toLowerCase()) {
      case 'evaluation':
        return 'Evaluation';
      case 'competition':
        return 'Competition';
      case 'in-house':
        return 'In-House';
      case 'deleted':
        return 'Deleted';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Scores', href: '#' },
    ...(statusFromUrl
      ? [
        {
          label: getStatusDisplayName(statusFromUrl),
          href: '#',
        },
      ]
      : []),
  ];

  const [scoreStatuses, setScoreStatuses] = useState<{ value: string; label: string }[]>([]);
  const [exerciseTypes, setExerciseTypes] = useState<{ value: string; label: string }[]>([]);
  const [activityTypes, setActivityTypes] = useState<{ value: string; label: string }[]>([]);

  const { data: scoreTypesData = [], isLoading: isLoadingScoreTypes } = useScoreTypes();
  const { data: scoreStatusesData, isLoading: isLoadingScoreStatuses } = useScoreStatuses();
  const { search: searchExerciseTypes, isLoading: isLoadingExerciseTypes } = useExerciseTypeApi();
  const { search: searchMemberFirearms, isLoading: isLoadingMemberFirearms } =
    useMemberFirearmApi();
  const { data: activityTypesData = [], isLoading: isLoadingActivityTypes } = useActivityTypes();

  useEffect(() => {
    setScoreStatuses(
      scoreStatusesData?.map((status) => ({
        value: status.id ?? '',
        label: status.title ?? '',
      })) || [],
    );
  }, [scoreStatusesData]);

  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        // Set initial filters based on URL status
        if (statusFromUrl) {
          const statusFilters = getFiltersFromStatus(statusFromUrl);
          setSearchFilters((prev: any) => ({
            ...prev,
            ...statusFilters,
          }));
        }

        const [exerciseTypes, memberFirearms] = await Promise.all([
          searchExerciseTypes({ page: 1, pageSize: 999, sortBy: 'DisplayPosition' }),
          searchMemberFirearms({ page: 1, pageSize: 999, sortBy: 'DisplayPosition' }),
        ]);

        setExerciseTypes(
          exerciseTypes?.data?.results?.map((type) => ({
            value: type.id ?? '',
            label: type.title ?? '',
          })) || [],
        );
        setActivityTypes(
          activityTypesData?.map((type) => ({
            value: type.id ?? '',
            label: type.title ?? '',
          })) || [],
        );
      } catch (error) {
        console.error('Error loading dropdown data:', error);
      }
    };

    loadDropdownData();
  }, [statusFromUrl]);

  // Reset then reload years when route changes
  useEffect(() => {
    setSelectedYear(null);
    setAvailableYears([]);
    if (!roles?.includes('User')) return;
    getDistinctYears().then((years) => {
      setAvailableYears(years);
      if (years.length > 0) setSelectedYear(years[0]);
    });
  }, [statusFromUrl]);



  const fetchItems = async (page: number, size: number, filters: any) => {
    console.log('[Score List] fetchItems called', { page, size, filters, itemsCount: items.length });
    try {
      const statusFilters = getFiltersFromStatus(statusFromUrl);
      const yearFilters = selectedYear
        ? {
          createdFrom: new Date(selectedYear, 0, 1).toISOString(),
          createdTo: new Date(selectedYear, 11, 31, 23, 59, 59).toISOString(),
        }
        : {};
      const response = await search({
        filters: {
          ...filters,
          ...statusFilters,
          ...yearFilters,
        },
        page,
        pageSize: size,
        sortBy: sortBy,
        sortDescending: sortDescending,
      });
      console.log('[Score List] fetchItems response received', {
        resultsCount: response?.data?.results?.length || 0,
        totalCount: response?.data?.totalCount || 0,
        filters
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
      return response?.data?.totalCount || 0;
    } catch (error) {
      console.error('Error fetching Rpls:', error);
      return 0;
    }
  };

  const previousFiltersRef = useRef<string>('');
  const isCommittingSearchRef = useRef<boolean>(false);
  const yearInitialisedRef = useRef<boolean>(false);

  // Refetch when selected year changes
  useEffect(() => {
    if (selectedYear === null) {
      if (yearInitialisedRef.current) {
        yearInitialisedRef.current = false;
        setCurrentPage(1);
        fetchItems(1, pageSize, searchFilters);
      }
      return;
    }
    yearInitialisedRef.current = true;
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // Sync check: get current URL filters on every render to detect mismatches
  const getCurrentUrlFilters = () => {
    const params = new URLSearchParams(location.search);
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const urlFilters: Record<string, string> = {};
    params.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        urlFilters[key] = value;
      }
    });
    return JSON.stringify(urlFilters);
  };

  const currentUrlFiltersKey = getCurrentUrlFilters();
  const areFiltersStale = items.length > 0 && previousFiltersRef.current !== currentUrlFiltersKey;

  console.log('[Score List] Render', {
    itemsCount: items.length,
    currentUrlFiltersKey,
    previousFiltersKey: previousFiltersRef.current,
    areFiltersStale,
    locationSearch: location.search
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const urlFilters: Record<string, string> = {};

    params.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        urlFilters[key] = value;
      }
    });

    const currentFiltersKey = JSON.stringify(urlFilters);

    console.log('[Score List] useEffect triggered', {
      locationSearch: location.search,
      isCommittingSearch: isCommittingSearchRef.current,
      currentFiltersKey,
      previousFiltersKey: previousFiltersRef.current,
      urlMatchesPrevious: currentFiltersKey === previousFiltersRef.current
    });

    // Skip this effect if we're in the middle of a commitSearch AND the URL hasn't actually changed yet
    // This prevents firing a fetch with stale URL params after commitSearch but before navigation completes
    if (isCommittingSearchRef.current && currentFiltersKey === previousFiltersRef.current) {
      console.log('[Score List] Skipping useEffect - commitSearch in progress but URL not updated yet');
      return; // Keep flag set, will check again on next render
    }

    // URL has now updated, clear the flag
    if (isCommittingSearchRef.current) {
      console.log('[Score List] URL updated after commitSearch, clearing flag');
      isCommittingSearchRef.current = false;
    }

    // Parse page and pageSize from URL (source of truth) to avoid stale state
    const urlPage = params.get('page');
    const urlPageSize = params.get('pageSize');
    const page = urlPage ? parseInt(urlPage, 10) : 1;
    const size = urlPageSize ? parseInt(urlPageSize, 10) : pageSize;

    // Clear items immediately if filters changed (not just page number)
    if (previousFiltersRef.current !== currentFiltersKey) {
      console.log('[Score List] Filters changed - clearing items', {
        oldFilters: previousFiltersRef.current,
        newFilters: currentFiltersKey
      });
      setItems([]);
      console.log('[Score List] setItems([]) called - items should now be empty');
      previousFiltersRef.current = currentFiltersKey;
    }

    fetchItems(page, size, {
      ...urlFilters,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, statusFromUrl]); // Only depend on URL and statusFromUrl, not hook state

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, {
      ...searchFilters,
    });
  };

  const handleMemberClick = (memberId: string) => {
    console.log('[Score List] handleMemberClick called', { memberId, currentItemsCount: items.length });
    // Find the score with this memberId to get the member's ID/Passport number
    const score = items.find(item => item.memberId === memberId);
    const idPassportNumber = score?.member?.idPassportNumber || '';

    console.log('[Score List] Calling commitSearch', {
      idPassportNumber,
      currentUrl: location.search
    });

    // Set flag to prevent intermediate useEffect from running with stale URL
    isCommittingSearchRef.current = true;

    // Build one focused search: clear all filters and search only by ID number.
    const newFilters = idPassportNumber ? { idPassportNumber } : {};
    // Commit atomically and push a history entry so Back returns to prior score search state.
    // The useEffect watching location.search will trigger the fetch automatically
    commitSearch(newFilters, { page: 1, showAdvanced: false, replace: false });

    console.log('[Score List] commitSearch completed', {
      newUrl: location.search,
      itemsStillPresent: items.length
    });
  };

  const handleClearSearch = () => {
    clearSearchState();
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: ScoreDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error deleting Score:', error);
    }
  };

  // Email handlers from useEmailHandlers hook
  const {
    handleSendEmail,
    handleSendEmailSubmit,
    handleViewAllEmails,
    handleViewRecordEmails,
  } = useEmailHandlers<ScoreDTO>({
    setSelectedItem: setSelectedScore,
    setDrawerTitle,
    setDrawerView,
    setIsDrawerOpen,
    sendEmail,
    fetchItems: async (page: number, size: number, filters: any) => {
      await fetchItems(page, size, filters);
    },
    currentPage,
    pageSize,
    searchFilters,
  });

  const handleRestore = async (score: ScoreDTO) => {
    try {
      await restoreFromDeleted(score.id ?? '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error restoring score:', error);
    }
  };

  const handleOpenDrawer = async (
    score: ScoreDTO,
    drawerView: ScoreDrawerViews,
    drawerTitle: string,
    drawerSize: 'small' | 'large' = 'large',
    defaultStatusId?: string,
  ) => {
    setSelectedScore(score);
    setDrawerView(drawerView);
    setDrawerTitle(drawerTitle);
    setDrawerSize(drawerSize);
    if (defaultStatusId) setDefaultStatusId(defaultStatusId);
    setIsDrawerOpen(true);
  };

  const handleAddNote = async (data: NoteDTO) => {
    try {
      setIsSubmitting(true);
      await createNote(data);
      setIsDrawerOpen(false);
      setSelectedScore({});
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error creating note: ', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add export handler
  const handleExport = async () => {
    try {
      const response = await extract({
        filters: searchFilters,
        page: 1,
        pageSize: 9999999, // Large number to export all matching records
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });

      if (response.data) {
        window.location.href = response.data;
      }
    } catch (error) {
      console.error('Error exporting Scores:', error);
    }
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedScore({});
    setDefaultStatusId(undefined);
    setShowBulkActions(false);
    setDrawerSize('large');
  };

  const handleStatusSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);

      // Determine which score IDs to update
      const idsToUpdate = selectedScoreIds.size > 0
        ? Array.from(selectedScoreIds)
        : [selectedScore.id!];

      // Prepare bulk update DTO
      const updateDto = {
        recordIds: idsToUpdate,
        scoreStatusId: data.scoreStatusId,
        feedback: data.feedback,
        replyTemplateId: data.replyTemplateId,
        sendFeedbackEmail: data.sendFeedbackEmail || false,
      };

      // Call the bulk update endpoint
      await updateStatus(updateDto);

      setIsDrawerOpen(false);
      setSelectedScore({});
      setSelectedScoreIds(new Set()); // Clear selections
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSort = (sortKey: string) => {
    if (sortBy === sortKey) {
      setSort(sortKey, !sortDescending);
    } else {
      setSort(sortKey, true);
    }
  };

  /**
   * Refreshes the list after a mutation (delete, update, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    // Fetch with current page first to get the new total count
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);

    // Calculate what page we should be on based on new total
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);

    // If we need to move to a different page, update and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    setSelectedScoreIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = items.map(item => item.id).filter((id): id is string => !!id);
      setSelectedScoreIds(new Set(allIds));
    } else {
      setSelectedScoreIds(new Set());
    }
  };

  const handleBulkStatusUpdate = () => {
    const firstSelectedId = Array.from(selectedScoreIds)[0];
    const firstScore = items.find(s => s.id === firstSelectedId) || {};
    const count = selectedScoreIds.size;
    handleOpenDrawer(
      firstScore as ScoreDTO,
      DrawerViewConstants.FEEDBACK,
      `Update Status (${count} score${count > 1 ? 's' : ''})`
    );
  };

  const handleBulkApprove = () => {
    const firstSelectedId = Array.from(selectedScoreIds)[0];
    const firstScore = items.find(s => s.id === firstSelectedId) || {};
    const count = selectedScoreIds.size;
    handleOpenDrawer(
      firstScore as ScoreDTO,
      DrawerViewConstants.FEEDBACK,
      `Approve (${count} score${count > 1 ? 's' : ''})`,
      'large',
      ScoreStatusConstants.APPROVED
    );
  };

  const handleBulkApproveWithFeedback = () => {
    const firstSelectedId = Array.from(selectedScoreIds)[0];
    const firstScore = items.find(s => s.id === firstSelectedId) || {};
    const count = selectedScoreIds.size;
    handleOpenDrawer(
      firstScore as ScoreDTO,
      DrawerViewConstants.FEEDBACK,
      `Approve with Feedback (${count} score${count > 1 ? 's' : ''})`,
      'large',
      ScoreStatusConstants.APPROVED_WITH_FEEDBACK
    );
  };

  const handleBulkAcknowledge = () => {
    const firstSelectedId = Array.from(selectedScoreIds)[0];
    const firstScore = items.find(s => s.id === firstSelectedId) || {};
    const count = selectedScoreIds.size;
    handleOpenDrawer(
      firstScore as ScoreDTO,
      DrawerViewConstants.FEEDBACK,
      `Acknowledge (${count} score${count > 1 ? 's' : ''})`,
      'large',
      ScoreStatusConstants.ACKNOWLEDGED
    );
  };

  const handleBulkReviewWithFeedback = () => {
    const firstSelectedId = Array.from(selectedScoreIds)[0];
    const firstScore = items.find(s => s.id === firstSelectedId) || {};
    const count = selectedScoreIds.size;
    handleOpenDrawer(
      firstScore as ScoreDTO,
      DrawerViewConstants.FEEDBACK,
      `Review with Feedback (${count} score${count > 1 ? 's' : ''})`,
      'large',
      ScoreStatusConstants.REVIEWED_WITH_FEEDBACK
    );
  };

  const handleBulkDecline = () => {
    const firstSelectedId = Array.from(selectedScoreIds)[0];
    const firstScore = items.find(s => s.id === firstSelectedId) || {};
    const count = selectedScoreIds.size;
    handleOpenDrawer(
      firstScore as ScoreDTO,
      DrawerViewConstants.FEEDBACK,
      `Decline (${count} score${count > 1 ? 's' : ''})`,
      'large',
      ScoreStatusConstants.DECLINED
    );
  };

  const handleBulkOutstanding = () => {
    const firstSelectedId = Array.from(selectedScoreIds)[0];
    const firstScore = items.find(s => s.id === firstSelectedId) || {};
    const count = selectedScoreIds.size;
    handleOpenDrawer(
      firstScore as ScoreDTO,
      DrawerViewConstants.FEEDBACK,
      `Mark Outstanding (${count} score${count > 1 ? 's' : ''})`,
      'large',
      ScoreStatusConstants.OUTSTANDING
    );
  };

  // Close bulk actions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bulkActionsRef.current && !bulkActionsRef.current.contains(event.target as Node)) {
        setShowBulkActions(false);
      }
    };

    if (showBulkActions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showBulkActions]);

  return (
    <AdminLayout
      activeMenuItem={statusFromUrl ? `/admin/scores/${statusFromUrl}` : '/admin/scores'}
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin', 'User']}
      requiredPermission="Manage.Score.View"
    >
      <Card
        title={`Manage Scores ${statusFromUrl ? `- ${getStatusDisplayName(statusFromUrl)}` : ''}`}
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex items-center space-x-2">
            {roles?.includes('Admin') && selectedScoreIds.size > 0 && (
              <div ref={bulkActionsRef} className="relative">
                <Button
                  variant="secondary"
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  className="flex items-center gap-2"
                >
                  Bulk Actions
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full">
                    {selectedScoreIds.size}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${showBulkActions ? 'rotate-180' : ''
                      }`}
                  />
                </Button>
                {showBulkActions && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                    <button
                      onClick={() => {
                        handleBulkStatusUpdate();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg"
                    >
                      Update Status
                    </button>
                    <button
                      onClick={() => {
                        handleBulkApprove();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        handleBulkApproveWithFeedback();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Approve with Feedback
                    </button>
                    <button
                      onClick={() => {
                        handleBulkAcknowledge();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Acknowledge
                    </button>
                    <button
                      onClick={() => {
                        handleBulkReviewWithFeedback();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Review with Feedback
                    </button>
                    <button
                      onClick={() => {
                        handleBulkDecline();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Decline
                    </button>
                    <button
                      onClick={() => {
                        handleBulkOutstanding();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 last:rounded-b-lg"
                    >
                      Mark Outstanding
                    </button>
                  </div>
                )}
              </div>
            )}
            {roles?.includes('Admin') && (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                >
                  {showAdvanced ? 'Basic Search' : 'Advanced Search'}
                </Button>
                <Button type="button" variant="secondary" onClick={handleExport}>
                  Export
                </Button>
              </>
            )}

            {roles?.includes('User') && (
              <>
                <ButtonLegendCta items={legendItems} />
                <AddNewButton label='Submit a Score' onClick={() => navigate('/admin/scores/create')} />
              </>
            )}
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          {showAdvanced && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <FormField
                id="firstName"
                label="Search by First Name"
                value={searchFilters.firstName || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    firstName: e.target.value,
                  })
                }
                placeholder="Search by First Name"
              />
              <FormField
                id="surname"
                label="Search by Surname"
                value={searchFilters.surname || ''}
                onChange={(e) => setSearchFilters({ ...searchFilters, surname: e.target.value })}
                placeholder="Search by Surname"
              />
              <FormField
                id="idPassportNumber"
                label="Search by ID Number"
                value={searchFilters.idPassportNumber || ''}
                onChange={(e) => setSearchFilters({ ...searchFilters, idPassportNumber: e.target.value })}
                placeholder="Search by Id Number"
              />
              <FormField
                id="scoreTypeId"
                label="Search by Score Type"
                value={searchFilters.scoreTypeId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    scoreTypeId: e.target.value,
                  })
                }
                placeholder="Select Score Type"
                options={scoreTypesData.map((type) => ({
                  value: type.id ?? '',
                  label: type.title ?? '',
                }))}
                isLoading={isLoadingScoreTypes}
              />
              <FormField
                id="scoreStatusId"
                label="Search by Score Status"
                value={searchFilters.scoreStatusId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    scoreStatusId: e.target.value,
                  })
                }
                placeholder="Select Score Status"
                options={scoreStatuses}
                isLoading={isLoadingScoreStatuses}
              />
              <FormField
                id="exerciseTypeId"
                label="Search by Exercise Type"
                value={searchFilters.exerciseTypeId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    exerciseTypeId: e.target.value,
                  })
                }
                placeholder="Select Exercise Type"
                options={exerciseTypes}
                isLoading={isLoadingExerciseTypes}
              />
              <FormField
                id="activityTypeId"
                label="Search by Activity Type"
                value={searchFilters.activityTypeId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    activityTypeId: e.target.value,
                  })
                }
                placeholder="Select Activity Type"
                options={activityTypes}
                isLoading={isLoadingActivityTypes}
              />
              <FormField
                id="location"
                label="Search by Location"
                value={searchFilters.location || ''}
                type="text"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    location: e.target.value,
                  })
                }
                placeholder="Search by Location"
              />
              <FormField
                id="createdFrom"
                label="Created From"
                type="date"
                value={searchFilters.createdFrom || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdFrom: e.target.value })
                }
              />
              <FormField
                id="createdTo"
                label="Created To"
                type="date"
                value={searchFilters.createdTo || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdTo: e.target.value })
                }
              />
            </div>
          )}

          <div className="flex justify-between items-center">
            {roles?.includes('User') && availableYears.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {availableYears.map((year) => (
                  <Button
                    key={year}
                    variant={selectedYear === year ? 'primary' : 'secondary'}
                    onClick={() => setSelectedYear(year)}
                  >
                    {year}
                  </Button>
                ))}
              </div>
            )}
            <div className="space-x-4 mb-4">
              {showAdvanced && (
                <>
                  <Button variant="secondary" onClick={handleClearSearch}>
                    Clear Search
                  </Button>
                  <Button type="submit">Search</Button>
                </>
              )}
            </div>

            {!showAdvanced && roles?.includes('Admin') && (
              <GeneralSearch
                searchFilters={searchFilters}
                setSearchFilters={setSearchFilters}
              />
            )}

            <div className="hidden md:block">{roles?.includes('User') && <ViewToggle view={view} toggle={toggle} />}</div>
          </div>
        </form>



        {roles?.includes('Admin') || view === ViewModeConstants.TABLE ? (
          <ScoreTable
            data={areFiltersStale ? [] : items}
            showCourseColumn={statusFromUrl === 'evaluation'}
            handleViewMemberDetails={(dto) => handleOpenDrawer(dto, DrawerViewConstants.VIEW_MEMBER_DETAILS, 'Member Details', 'small')}
            handleEdit={(dto) => navigate(`/admin/scores/${dto.id}`)}
            handleDelete={handleDelete}
            handleSendEmail={handleSendEmail}
            handleViewAllEmails={handleViewAllEmails}
            handleViewRecordEmails={handleViewRecordEmails}
            handleRestore={handleRestore}
            handleApprove={(dto) =>
              handleOpenDrawer(dto, DrawerViewConstants.FEEDBACK, 'Approve', 'large', ScoreStatusConstants.APPROVED)
            }
            handleChangeStatus={(dto) =>
              handleOpenDrawer(dto, DrawerViewConstants.FEEDBACK, 'Change Status')
            }
            handleImpersonateMemberLogin={(dto) => impersonate(dto.memberId, dto.member?.memberStatusId)}
            handleAddNote={(dto) =>
              handleOpenDrawer(dto, DrawerViewConstants.ADD_NOTE, 'Add Note')
            }
            handleViewNoteHistory={(dto) =>
              handleOpenDrawer(dto, DrawerViewConstants.VIEW_NOTE_HISTORY, 'Notes History')
            }
            handleMemberClick={handleMemberClick}
            currentPage={currentPage}
            totalPages={Math.ceil(totalRecords / pageSize)}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            isLoading={isLoading}
            sortBy={sortBy}
            sortDescending={sortDescending}
            onSort={handleSort}
            selectedIds={roles?.includes('Admin') ? selectedScoreIds : undefined}
            onSelectRow={roles?.includes('Admin') ? handleSelectRow : undefined}
            onSelectAll={roles?.includes('Admin') ? handleSelectAll : undefined}
          />
        ) : (
          <ScoreDataCard
            data={areFiltersStale ? [] : items}
            handleEdit={(dto) => navigate(`/admin/scores/${dto.id}`)}
            handleDelete={handleDelete}
            currentPage={currentPage}
            totalPages={Math.ceil(totalRecords / pageSize)}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            isLoading={isLoading}
          />
        )}

        <Drawer isOpen={isDrawerOpen} onClose={handleDrawerClose} title={drawerTitle} size={drawerSize}>
          {drawerView === DrawerViewConstants.VIEW_MEMBER_DETAILS && (
            <MemberQuickDetails
              memberId={selectedScore.member?.id ?? ''}
              onClose={handleDrawerClose}
            />
          )}
          {drawerView === DrawerViewConstants.FEEDBACK && (
            <FeedbackForm
              componentId={ComponentConstants.SCORES}
              recordId={selectedScore.id ?? ''}
              componentStatuses={scoreStatuses}
              statusFieldName={statusFieldName}
              defaultStatusId={defaultStatusId}
              initialData={selectedScore}
              onSubmit={handleStatusSubmit}
              onCancel={handleDrawerClose}
              updateLoading={isSubmitting}
            />
          )}
          {drawerView === DrawerViewConstants.ADD_NOTE && (
            <AddNote
              memberId={selectedScore.member?.id || ''}
              componentId={ComponentConstants.SCORES}
              recordId={selectedScore.id ?? ''}
              onSubmit={handleAddNote}
              onCancel={handleDrawerClose}
              isLoading={isSubmitting}
            />
          )}
          {drawerView === DrawerViewConstants.VIEW_NOTE_HISTORY && (
            <ViewNoteHistory memberId={selectedScore.member?.id || ''} />
          )}
          <EmailDrawerContent
            drawerView={drawerView}
            selectedItem={selectedScore}
            componentId={ComponentConstants.SCORES}
            getMemberId={(dto) => dto?.member?.id || ''}
            onEmailSubmit={handleSendEmailSubmit}
            onClose={handleDrawerClose}
            isSending={isSendingEmail}
          />
        </Drawer>
      </Card>
    </AdminLayout>
  );
};

export default ScoreList;
